/**
 * Admin Landing / Portal Page
 * Uses shared landing components but styled for operational / internal tone.
 * Not consumer marketing — clean, authoritative, system-management focused.
 *
 * This is a gated internal page. It exists at /admin within the appropriate app.
 * For now it's created as a standalone page component to be mounted where needed.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Shield, Users, BarChart3, Settings,
  Database, Lock, Activity, Eye, FileText,
} from 'lucide-react';
import { Button } from '@bright-web-skeleton/ui';
import {
  LandingHeroSection,
  LandingSteps,
  LandingBenefitsGrid,
  LandingFooterSection,
} from '@bright-web-skeleton/ui';

export const AdminLandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background" data-theme="koordinator">
      {/* ─── Minimal top bar ─── */}
      <nav className="border-b border-border/30 bg-background/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-foreground rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-background" />
              </div>
              <span className="font-bold text-xl tracking-tight text-foreground">Admin Portal</span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── Hero — authoritative, not flashy ─── */}
      <LandingHeroSection
        className="bg-muted/20"
        badge="Platform Administration"
        badgeIcon={<Shield className="w-4 h-4" />}
        title={
          <span className="text-foreground">
            Kloutforce <span className="text-muted-foreground">Admin Portal</span>
          </span>
        }
        description="Manage users, roles, approvals, payouts, and platform operations. This portal is restricted to authorized administrators."
        primaryAction={
          <Link to="/login">
            <Button size="lg" className="gap-2 px-8 text-base">
              <Lock className="w-4 h-4" /> Access Admin Panel
            </Button>
          </Link>
        }
      />

      {/* ─── Capabilities — not "How It Works" but "What You Can Do" ─── */}
      <LandingSteps
        heading="Admin Capabilities"
        subheading="Core operations available in the admin panel"
        steps={[
          {
            step: '01',
            icon: <Users className="h-7 w-7" />,
            title: 'User & Role Management',
            description: 'View, approve, suspend, or modify user accounts, agent profiles, partner profiles, and GA assignments.',
          },
          {
            step: '02',
            icon: <BarChart3 className="h-7 w-7" />,
            title: 'Financial Operations',
            description: 'Review earnings, process payouts, manage settlements, and handle recovery ledger items.',
          },
          {
            step: '03',
            icon: <Activity className="h-7 w-7" />,
            title: 'Platform Monitoring',
            description: 'Audit logs, commerce events, system health, and operational alerts — all in one view.',
          },
        ]}
      />

      {/* ─── Admin feature areas ─── */}
      <LandingBenefitsGrid
        heading="Administration Areas"
        subheading="Operational tools organized by domain"
        benefits={[
          {
            icon: <Users className="h-6 w-6" />,
            title: 'Agent Approvals',
            description: 'Review agent applications, approve or reject, and manage activation status.',
          },
          {
            icon: <Shield className="h-6 w-6" />,
            title: 'GA Management',
            description: 'Assign General Agents to countries, manage territory conflicts, and oversee GA requests.',
          },
          {
            icon: <Database className="h-6 w-6" />,
            title: 'Brand Operations',
            description: 'Manage brand verification, store connections, Stripe Connect status, and settlement requests.',
          },
          {
            icon: <Eye className="h-6 w-6" />,
            title: 'Audit Logs',
            description: 'Full trail of admin actions, role changes, payout approvals, and security events.',
          },
          {
            icon: <FileText className="h-6 w-6" />,
            title: 'Commission Config',
            description: 'Set global commission rates, override agent/partner percentages, and manage fee structures.',
          },
          {
            icon: <Settings className="h-6 w-6" />,
            title: 'System Configuration',
            description: 'Feature flags, invite quotas, activation fee settings, and platform-wide defaults.',
          },
        ]}
      />

      {/* ─── Footer — minimal ─── */}
      <LandingFooterSection
        logo={
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-foreground rounded-lg flex items-center justify-center">
              <Shield className="w-3.5 h-3.5 text-background" />
            </div>
            <span className="font-bold text-lg">Admin Portal</span>
          </div>
        }
        tagline="Kloutforce platform administration."
        columns={[
          {
            heading: 'Quick Links',
            links: [
              { label: 'Sign In', href: '/login' },
              { label: 'Support', href: '#' },
            ],
          },
        ]}
        copyright={`© ${new Date().getFullYear()} Kloutforce. Internal use only.`}
      />
    </div>
  );
};

export default AdminLandingPage;
